import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  TableCell,
  TableRow,
  IconButton,
  CircularProgress,
  Typography,
  Table,
  TableContainer,
  Paper,
} from '@mui/material'
import ProcessTimeline from './ProcessTimeline'
import { GetApp, Visibility } from '@mui/icons-material'
import { useAuthContext } from '../../hooks/useAuthContext'
import { FIELD_TYPES, FORMATS } from '../../utility/constants'
import moment from 'moment'
import LongMenu from '../../pages/DataEntryPage/Fields/LongMenu'
import FileViewer from './PreviewComponent/components/FileViewer'
import { activityTemplateService } from '../../services/activityTemplateService'
import { formatCurrency } from '../../pages/DataEntryPage/utils/currency'
import MultilineEllipsisWithTooltip from './MultilineEllipsisWithTooltip'
import { extractDisplayName } from '../../utility/fileUtil'
import TimeStampedBigTextViewer from './TimeStampedBigTextViewer'
import { TableVirtuoso } from 'react-virtuoso'

const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL
const INFO_COLUMN_WIDTH = 50
const SECOND_COLUMN_LEFT = INFO_COLUMN_WIDTH

const ProcessDashboardValues = ({
  processData,
  process_template_id,
  hiddenColumns,
  currencyForDashboardValues,
  hiddenActivityColumns,
  processTemplates,
  processDataForProcessTemplate,
  companyDetails,
  usersList,
  processDashBoardData,
  defaultTableHeaders,
  fieldTemplatesAll,
  defaultActivityTableHeaders,
  fieldTemplates,
  renderedActivityGroupHeaders,
  renderedDefaultHeaders,
  renderedFieldHeaders,
  renderedActivityHeaders,
  grayHeaderColor,
  secondHeaderTop,
  onEndReached,
  filterRowTop,
  showFilterIcons,
  renderFilterComponent,
  hiddenColumnsSet,
  hiddenActivityColumnsSet,
  virtuosoRef,
}) => {
  const { user } = useAuthContext()

  const [loading, setLoading] = useState(new Set())
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false)
  const [selectedFileKey, setSelectedFileKey] = useState(null)
  const [templateRoutes, setTemplateRoutes] = useState(null)
  const [isTSViewerOpen, setIsTSViewerOpen] = useState(false)
  const [tsViewerEntries, setTsViewerEntries] = useState([])
  const [tsViewerTitle, setTsViewerTitle] = useState('')

  const activityTemplatesAll = processDashBoardData?.activityTemplatesAll || []

  useEffect(() => {
    const load = async () => {
      const res = await activityTemplateService.getProcessAllRoutes(user, process_template_id)
      setTemplateRoutes(res.data)
    }
    if (process_template_id) load()
  }, [process_template_id, user])

  const badgeMap = React.useMemo(() => {
    const map = {}
    processDataForProcessTemplate?.forEach((it) => {
      map[it.process_reference_num] = (map[it.process_reference_num] || 0) + 1
    })
    return map
  }, [processDataForProcessTemplate])

  const currencyMap = React.useMemo(() => {
    const map = {}
    currencyForDashboardValues.forEach((c) => {
      if (!map[c.process_reference_num]) map[c.process_reference_num] = {}
      map[c.process_reference_num][c.field_template_id] = c.currency_code
    })
    return map
  }, [currencyForDashboardValues])

  const userMap = React.useMemo(() => {
    const map = {}
    usersList?.forEach((u) => {
      map[u.id] = u.value?.replace(/\s*\([^)]*\)\s*/g, '').trim() ?? ''
    })
    return map
  }, [usersList])

  const axiosOptionsWithBlob = useMemo(() => {
    return user
      ? {
          headers: { Authorization: `Bearer ${user.token}` },
          responseType: 'blob',
        }
      : {}
  }, [user])

  const getColumnWidth = useCallback(
    (colIndex) => {
      if (colIndex === 0) return `${INFO_COLUMN_WIDTH}px`

      const defaultHeadersCount = defaultTableHeaders?.length || 0

      if (colIndex >= 1 && colIndex <= defaultHeadersCount) {
        const headerIndex = colIndex - 1
        return defaultTableHeaders?.[headerIndex]?.width || 'auto'
      }

      const visibleFields =
        fieldTemplatesAll?.filter((f) => !f.is_hidden && !hiddenColumns?.includes(f.id)) || []

      const fieldStart = defaultHeadersCount + 1
      const fieldCount = visibleFields.length
      const fieldEnd = fieldStart + fieldCount

      if (colIndex >= fieldStart && colIndex < fieldEnd) {
        const fieldIndex = colIndex - fieldStart
        return visibleFields[fieldIndex]
          ? `${visibleFields[fieldIndex].field_width_in_rem_for_process_dashboard}rem`
          : 'auto'
      }

      const activityIndex = colIndex - fieldEnd
      return defaultActivityTableHeaders?.[activityIndex]?.width || '6rem'
    },
    [defaultTableHeaders, fieldTemplatesAll, hiddenColumns, defaultActivityTableHeaders],
  )

  const getFileWithS3Key = useCallback(
    async (s3Key, index) => {
      setLoading((prev) => new Set(prev).add(index))

      try {
        const result = await axios.get(
          `${NODE_BASE_URL}/data-entry/file/${s3Key}`,
          axiosOptionsWithBlob,
        )

        const url = window.URL.createObjectURL(new Blob([result.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', extractDisplayName(s3Key))
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } finally {
        setLoading((prev) => {
          const updated = new Set(prev)
          updated.delete(index)
          return updated
        })
      }
    },
    [axiosOptionsWithBlob],
  )

  const getProcessTimelineBadgeValue = (ref) => badgeMap[ref] || 0

  const openTimestampedViewer = (entries, title) => {
    setTsViewerEntries(Array.isArray(entries) ? entries : [])
    setTsViewerTitle(title || 'Timestamped Entries')
    setIsTSViewerOpen(true)
  }

  const handlePreviewClick = (fileKey) => {
    setSelectedFileKey(fileKey)
    setIsFileViewerOpen(true)
  }

  const getUserName = (update) => (update?.user_id ? userMap[update.user_id] || '' : '')

  const getOptions = useCallback(
    (x, y) => [
      {
        label: 'Preview',
        icon: (
          <IconButton size="small">
            <Visibility />
          </IconButton>
        ),
        onClick: () => handlePreviewClick(x),
      },
      {
        label: 'Download',
        icon: loading.has(y) ? <CircularProgress size={20} /> : <GetApp />,
        onClick: () => getFileWithS3Key(x, y),
      },
    ],
    [loading, getFileWithS3Key],
  )

  const renderCell = (pd, i, j, rowBg) => {
    const columnWidth = getColumnWidth(i)

    const textAlign =
      pd.fieldType === FIELD_TYPES.DATE ||
      pd.fieldType === FIELD_TYPES.DATETIME ||
      pd.fieldType === FIELD_TYPES.NUMBER ||
      pd.fieldType === FIELD_TYPES.CURRENCY
        ? 'right'
        : 'left'

    const stickyStyle =
      i === 0
        ? { position: 'sticky', left: 0, backgroundColor: rowBg, minWidth: columnWidth }
        : i === 1
          ? {
              position: 'sticky',
              left: `${INFO_COLUMN_WIDTH}px`,
              backgroundColor: rowBg,
              minWidth: columnWidth,
            }
          : { minWidth: columnWidth, textAlign }

    const fieldCurrencyMap = pd.id ? currencyMap[pd.process_reference_num]?.[pd.id] : undefined

    const isHidden =
      pd.is_hidden ||
      (pd?.is_Activity ? hiddenActivityColumns?.includes(pd.id) : hiddenColumns?.includes(pd.id))

    if (i === 0) {
      return (
        <TableCell key={i} sx={stickyStyle}>
          <ProcessTimeline
            processReferanceNum={pd.value}
            processTemplateId={process_template_id}
            templateRoutes={templateRoutes}
            processTemplates={processTemplates}
            processTimeLineBadge={getProcessTimelineBadgeValue(pd.value)}
          />
        </TableCell>
      )
    }

    if (isHidden) return null

    switch (pd.fieldType) {
      case FIELD_TYPES.FILE:
        return (
          <TableCell key={i} sx={stickyStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              {extractDisplayName(pd.value)}
              <LongMenu options={getOptions(pd.value, j)} />
            </div>
          </TableCell>
        )

      case FIELD_TYPES.TIMESTAMPED_BIGTEXT: {
        const entries = Array.isArray(pd.value) ? pd.value : []
        const first = entries[0]
        const more = entries.length > 1
        return (
          <TableCell
            key={i}
            sx={stickyStyle}
            onClick={() => openTimestampedViewer(entries, pd.label || pd.name)}
            style={{ cursor: entries.length ? 'pointer' : 'default' }}
          >
            <Typography variant="caption" sx={{ minWidth: 320 }}>
              {first && (
                <>
                  <b>{first.timestamp}:</b> {first.text}{' '}
                  {getUserName(first) && <i>- {getUserName(first)}</i>}
                  {more && <i> (+{entries.length - 1} more)</i>}
                </>
              )}
            </Typography>
          </TableCell>
        )
      }

      case FIELD_TYPES.CURRENCY:
        return (
          <TableCell key={i} align="right" sx={stickyStyle}>
            <Typography variant="caption" style={{ fontWeight: 600, color: '#4B4F60' }}>
              {formatCurrency(pd.value, fieldCurrencyMap, {
                defaultCurrency: companyDetails?.default_currency_code,
              })}
            </Typography>
          </TableCell>
        )

      case FIELD_TYPES.LIST:
      case FIELD_TYPES.LIST_MULTI:
        return (
          <TableCell key={i} sx={stickyStyle}>
            <MultilineEllipsisWithTooltip
              text={Array.isArray(pd.value) ? pd.value.join('\n\n') : pd.value}
              maxLines={3}
            />
          </TableCell>
        )

      case FIELD_TYPES.DATE: {
        const val = moment(new Date(pd.value)).format(FORMATS.DATE)
        return (
          <TableCell key={i} align="right" sx={stickyStyle}>
            <Typography variant="caption" style={{ fontWeight: 600, color: '#4B4F60' }}>
              {val !== 'Invalid date' ? val : ''}
            </Typography>
          </TableCell>
        )
      }

      case FIELD_TYPES.DATETIME: {
        const val = moment(new Date(pd.value)).format(FORMATS.DATETIME)
        return (
          <TableCell key={i} align="right" sx={stickyStyle}>
            <Typography variant="caption" style={{ fontWeight: 600, color: '#4B4F60' }}>
              {val !== 'Invalid date' ? val : ''}
            </Typography>
          </TableCell>
        )
      }

      default:
        return (
          <TableCell key={i} sx={{ ...stickyStyle, textAlign }}>
            <Typography variant="caption">
              <MultilineEllipsisWithTooltip text={pd.value} maxLines={3} />
            </Typography>
          </TableCell>
        )
    }
  }

  // ─── KEY FIX: memoize renderFilterRow so its reference is stable ───────────
  const renderFilterRow = useCallback(() => {
    if (!showFilterIcons) return null

    return (
      <TableRow
        style={{
          position: 'sticky',
          top: filterRowTop - 1,
          backgroundColor: '#fff',
          zIndex: 3,
        }}
      >
        {/* Info column — sticky left, no filter */}
        <TableCell
          style={{
            padding: '8px',
            width: INFO_COLUMN_WIDTH,
            minWidth: INFO_COLUMN_WIDTH,
            position: 'sticky',
            left: 0,
            backgroundColor: '#fff',
            zIndex: 5,
          }}
        />

        {/* Default process metadata headers */}
        {defaultTableHeaders.map((header, index) => (
          <TableCell
            key={index}
            style={{
              padding: '8px',
              ...(index === 0 && {
                position: 'sticky',
                left: SECOND_COLUMN_LEFT,
                backgroundColor: '#fff',
                zIndex: 5,
              }),
            }}
          >
            {renderFilterComponent(index, header.data_structure)}
          </TableCell>
        ))}

        {/* Process field template columns */}
        {fieldTemplatesAll?.length > 0 &&
          fieldTemplatesAll.map(
            (f, i) =>
              !f.is_hidden &&
              !hiddenColumnsSet?.has(f.id) && (
                <TableCell
                  id={'sf_' + (i + defaultTableHeaders.length)}
                  key={i + defaultTableHeaders.length}
                  style={{ padding: '8px' }}
                >
                  {renderFilterComponent(i + defaultTableHeaders.length, f.data_structure)}
                </TableCell>
              ),
          )}

        {/* Activity columns */}
        {(() => {
          let visibleActivityIndex = 0
          return (
            activityTemplatesAll?.length > 0 &&
            activityTemplatesAll.map((data) => {
              if (hiddenActivityColumnsSet?.has(data.id)) return null
              const currentActivityIndex = visibleActivityIndex++
              return defaultActivityTableHeaders.map((header, i) => {
                const filterIndex =
                  i +
                  defaultTableHeaders.length +
                  fieldTemplatesAll.length +
                  currentActivityIndex * defaultActivityTableHeaders.length
                return (
                  <TableCell key={`${data.id}-${i}`} style={{ padding: '8px' }}>
                    {renderFilterComponent(filterIndex, header.data_structure)}
                  </TableCell>
                )
              })
            })
          )
        })()}
      </TableRow>
    )
  }, [
    showFilterIcons,
    filterRowTop,
    defaultTableHeaders,
    fieldTemplatesAll,
    hiddenColumnsSet,
    activityTemplatesAll,
    hiddenActivityColumnsSet,
    defaultActivityTableHeaders,
    renderFilterComponent,
  ])

  // Memoize fixedHeaderContent so TableVirtuoso never remounts the header
  const fixedHeaderContent = useMemo(
    () => () => (
      <>
        {/* Row 1: Group headers — Process Metadata | Process Data | Timeline: ... */}
        <TableRow>
          <TableCell
            align="center"
            sx={{ backgroundColor: grayHeaderColor }}
            colSpan={defaultTableHeaders.length + 1}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 'bolder',
                letterSpacing: '1px',
                fontSize: '16px',
                color: '#6c47e4',
              }}
            >
              Process MetaData
            </Typography>
          </TableCell>

          {fieldTemplates?.length > 0 && (
            <TableCell
              align="center"
              sx={{ backgroundColor: grayHeaderColor }}
              colSpan={fieldTemplates.length}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 'bolder',
                  letterSpacing: '1px',
                  fontSize: '16px',
                  color: '#6c47e4',
                }}
              >
                Process Data
              </Typography>
            </TableCell>
          )}

          {renderedActivityGroupHeaders}
        </TableRow>

        {/* Row 2: Individual column headers */}
        <TableRow>
          <TableCell
            align="left"
            sx={{
              width: INFO_COLUMN_WIDTH,
              minWidth: INFO_COLUMN_WIDTH,
              position: 'sticky',
              left: 0,
              zIndex: 10,
              backgroundColor: grayHeaderColor,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              Info
            </Typography>
          </TableCell>

          {renderedDefaultHeaders}
          {renderedFieldHeaders}
          {renderedActivityHeaders}
        </TableRow>

        {/* Row 3: Filter inputs — rendered only when showFilterIcons is true */}
        {renderFilterRow()}
      </>
    ),
    [
      grayHeaderColor,
      defaultTableHeaders,
      fieldTemplates,
      renderedActivityGroupHeaders,
      renderedDefaultHeaders,
      renderedFieldHeaders,
      renderedActivityHeaders,
      renderFilterRow,
    ],
  )

  // Stable components object — must not be recreated on every render
  const virtuosoComponents = useMemo(
    () => ({
      Scroller: React.forwardRef((props, ref) => (
        <TableContainer component={Paper} ref={ref} {...props} />
      )),
      Table: (props) => (
        <Table
          {...props}
          stickyHeader
          sx={{
            tableLayout: 'fixed',
            width: 'auto',
            minWidth: '100%',
          }}
        />
      ),
    }),
    [],
  )

  return (
    <>
      <TableVirtuoso
        ref={virtuosoRef}
        style={{ height: '75vh', width: '100%' }}
        data={processData}
        endReached={onEndReached}
        components={virtuosoComponents}
        fixedHeaderContent={fixedHeaderContent}
        itemContent={(rowIndex, pdr) => pdr.map((pd, i) => renderCell(pd, i, rowIndex, '#fff'))}
      />

      <FileViewer
        open={isFileViewerOpen}
        onClose={() => setIsFileViewerOpen(false)}
        fileKey={selectedFileKey}
        user={user}
        onDownload={
          selectedFileKey ? () => getFileWithS3Key(selectedFileKey, selectedFileKey) : undefined
        }
      />

      <TimeStampedBigTextViewer
        open={isTSViewerOpen}
        onClose={() => setIsTSViewerOpen(false)}
        title={tsViewerTitle}
        entries={tsViewerEntries}
        resolveUserName={getUserName}
      />
    </>
  )
}

export default React.memo(ProcessDashboardValues)
